<section class="space-y-8">
  <header class="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-5">
    <div>
      <p class="font-mono text-2xs uppercase tracking-wider text-muted-foreground">Kundenansicht & Übergabe</p>
      <h2 class="mt-1 text-lg font-bold tracking-tight text-foreground">Vertrieb vorbereiten</h2>
      <p class="mt-1 max-w-2xl text-sm text-muted-foreground">Prüfen Sie den Bericht, aktivieren Sie den Kundenlink und teilen Sie anschließend die fertige Analyse.</p>
    </div>
    <div class="flex items-center gap-2">
      <span class="text-xs text-muted-foreground">Vertriebsstatus</span>
      <span [class]="'inline-flex rounded-full border px-2.5 py-1 text-xs font-normal ' + businessStatus().badgeClass">{{ businessStatus().label }}</span>
    </div>
  </header>

  <div class="grid gap-10 xl:grid-cols-[minmax(0,1fr)_320px] xl:items-start">
    <div class="min-w-0 space-y-9">
      <section>
        <div class="mb-3 flex items-center justify-between gap-4">
          <h3 class="text-sm font-bold text-foreground">Analyse</h3>
          <span class="text-xs text-muted-foreground">{{ businessStatus().description }}</span>
        </div>
        <dl class="grid border-y border-border sm:grid-cols-3 sm:divide-x sm:divide-border">
          <div class="py-4 sm:pr-5">
            <dt class="font-mono text-2xs uppercase tracking-wider text-muted-foreground">Objekt</dt>
            <dd class="mt-2 text-dense leading-relaxed text-foreground">
              @if (analysis().address) {
                {{ analysis().address?.street }} {{ analysis().address?.houseNumber }}<br />
                <span class="text-muted-foreground">{{ analysis().address?.postalCode }} {{ analysis().address?.city }}</span>
              } @else { Keine Adresse hinterlegt }
            </dd>
          </div>
          <div class="border-t border-border py-4 sm:border-t-0 sm:px-5">
            <dt class="font-mono text-2xs uppercase tracking-wider text-muted-foreground">Erstellt</dt>
            <dd class="mt-2 font-mono text-[12px] text-foreground">{{ analysis().createdAt | date: "dd.MM.yyyy, HH:mm" }}</dd>
          </div>
          <div class="border-t border-border py-4 sm:border-t-0 sm:pl-5">
            <dt class="font-mono text-2xs uppercase tracking-wider text-muted-foreground">Aktualisiert</dt>
            <dd class="mt-2 font-mono text-[12px] text-foreground">{{ analysis().updatedAt | date: "dd.MM.yyyy, HH:mm" }}</dd>
          </div>
        </dl>
      </section>

      <section>
        <div class="mb-3 flex items-center justify-between gap-4">
          <h3 class="text-sm font-bold text-foreground">Kundenbericht</h3>
          <span class="font-mono text-2xs uppercase tracking-wider text-muted-foreground">Schritt 1</span>
        </div>
        <button type="button" class="group flex w-full items-center gap-3 border-y border-border py-4 text-left transition-colors hover:bg-muted/30" (click)="filesRequested.emit()">
          <span class="grid size-9 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground group-hover:text-foreground">
            <ng-icon name="lucideFileText" size="17" />
          </span>
          <span class="min-w-0 flex-1">
            <span class="block truncate text-dense font-normal text-foreground">{{ reportFile()?.file?.filename || "Analysebericht" }}</span>
            <span class="mt-0.5 block text-xs text-muted-foreground">{{ reportFile() ? "Report prüfen oder herunterladen" : "Noch kein Report erstellt" }}</span>
          </span>
          <span class="font-mono text-2xs uppercase tracking-wider text-muted-foreground">Dateien öffnen</span>
        </button>
      </section>

      <section>
        <div class="mb-3 flex items-center gap-2">
          <h3 class="text-sm font-bold text-foreground">Verlauf</h3>
          <span class="font-mono text-2xs text-muted-foreground">0 Einträge</span>
        </div>
        <div class="border-l-2 border-border py-3 pl-5">
          <p class="text-dense font-normal text-foreground">Noch keine Aktivität aufgezeichnet</p>
          <p class="mt-1 text-xs text-muted-foreground">Statusänderungen und Kundenfreigaben erscheinen hier.</p>
        </div>
      </section>
    </div>

    <aside class="space-y-8 xl:border-l xl:border-border xl:pl-8">
      <section>
        <div class="flex items-center justify-between gap-3">
          <div>
            <p class="font-mono text-2xs uppercase tracking-wider text-muted-foreground">Schritt 2</p>
            <h3 class="mt-1 text-sm font-bold text-foreground">Kundenfreigabe</h3>
          </div>
          <hlm-switch aria-label="Öffentlichen Link aktivieren" [ngModel]="analysis().publicAccessEnabled" [disabled]="isToggling() || !canEdit()" (ngModelChange)="togglePublicAccess($event)" />
        </div>

        @if (canEdit()) {
          <div class="mt-3 flex flex-wrap items-center gap-2 text-sm">
            <span>Gültigkeit ab Freigabe</span>
            <app-select-field class="w-36" [options]="accessMonthOptions" [value]="'' + publicAccessMonths" (selected)="publicAccessMonths = $event === '6' ? 6 : 12" />
            @if (analysis().publicAccessEnabled) {
              <button hlmBtn variant="outline" size="sm" [disabled]="isToggling()" (click)="togglePublicAccess(true)">Frist erneuern</button>
            }
          </div>
        }
        @if (analysis().publicAccessExpiresAt) {
          <p class="mt-2 text-xs text-muted-foreground">Ablauf: {{ analysis().publicAccessExpiresAt | date:'dd.MM.yyyy' }}.</p>
        }
        @if (!analysis().publicAccessEnabled) {
          <div class="mt-5 border-l-2 border-primary/30 pl-4">
            <p class="text-dense font-normal text-foreground">Kundenlink aktivieren</p>
            <p class="mt-1 text-xs leading-relaxed text-muted-foreground">Der Link ist mit Postleitzahl und Hausnummer geschützt und kann jederzeit wieder deaktiviert werden.</p>
          </div>
        } @else if (linkError()) {
          <div role="alert" class="mt-5 border-l-2 border-destructive/50 pl-4">
            <p class="text-dense font-normal text-destructive">Link konnte nicht geladen werden</p>
            <p class="mt-1 text-xs leading-relaxed text-muted-foreground">{{ linkError() }}</p>
            <button hlmBtn size="sm" variant="ghost" class="mt-2 -ml-2 h-7 gap-1.5 px-2 text-xs" (click)="retryPublicLink()">
              <ng-icon name="lucideRefreshCw" size="13" />
              Erneut versuchen
            </button>
          </div>
        } @else if (!publicLink()) {
          <p class="mt-5 text-xs text-muted-foreground">Kundenlink wird erstellt …</p>
        } @else {
          <div class="mt-5 flex flex-col items-center text-center">
            <div class="rounded-lg border border-border bg-white p-1.5">
              <qrcode [qrdata]="publicLink()" [width]="116" [errorCorrectionLevel]="'M'" />
            </div>
            <div class="mt-3 flex w-full items-center gap-1.5 rounded-lg bg-muted/60 p-1 pl-2.5">
              <span class="min-w-0 flex-1 truncate text-left font-mono text-2xs text-muted-foreground" [title]="publicLink()">{{ publicLink() }}</span>
              <button hlmBtn size="sm" variant="ghost" class="!size-7 rounded-md" (click)="copyLink()" aria-label="Kundenlink kopieren">
                <ng-icon [name]="copied() ? 'lucideCheck' : 'lucideCopy'" size="13" />
              </button>
            </div>
            <button hlmBtn size="sm" class="mt-3 w-full rounded-lg" (click)="openLink()">
              <ng-icon name="lucideExternalLink" size="14" class="mr-2" />
              Kundenseite öffnen
            </button>
            <div class="mt-3 flex items-start gap-2 text-left text-2xs leading-relaxed text-muted-foreground">
              <ng-icon name="lucideShieldAlert" size="13" class="mt-0.5 shrink-0" />
              <span>Zugriff ist mit Postleitzahl und Hausnummer geschützt.</span>
            </div>
          </div>
        }
      </section>

      <section class="border-t border-border pt-6">
        <p class="font-mono text-2xs uppercase tracking-wider text-muted-foreground">Kontakt</p>
        <div class="mt-4 flex items-start gap-3">
          <span class="grid size-8 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground">
            <ng-icon name="lucideMapPin" size="15" />
          </span>
          <div class="min-w-0 text-dense leading-relaxed text-foreground">
            @if (analysis().address) {
              <p class="font-normal">{{ analysis().address?.street }} {{ analysis().address?.houseNumber }}</p>
              <p class="text-muted-foreground">{{ analysis().address?.postalCode }} {{ analysis().address?.city }}</p>
              <p class="text-muted-foreground">{{ analysis().address?.country }}</p>
            } @else { <p class="text-muted-foreground">Keine Kontaktdaten hinterlegt</p> }
          </div>
        </div>
      </section>
    </aside>
  </div>
</section>
